/**
 * BackgroundRemovalPanel.jsx - Panel de remoción de fondo
 * BACKGROUND_REMOVAL_PRO_V1
 * Con controles de refinamiento de bordes
 */

import { useState } from 'react';
import { Check, Layers, SlidersHorizontal } from 'lucide-react';

const BackgroundRemovalPanel = ({ 
  onApply, 
  isLoading,
  language = 'es' 
}) => {
  const [mode, setMode] = useState('white');
  const [backgroundPrompt, setBackgroundPrompt] = useState('');
  const [edgeSharpness, setEdgeSharpness] = useState(60);
  const [outlineWidth, setOutlineWidth] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleApply = () => {
    onApply(mode, backgroundPrompt, { edgeSharpness, outlineWidth });
  };

  return (
    <div
      data-testid="bg-panel-outer"
      className="flex items-center gap-2 flex-wrap sm:flex-wrap max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:w-full max-sm:pb-1 max-sm:[&::-webkit-scrollbar]:hidden"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* Icono y título */}
      <div className="flex items-center gap-1.5 text-violet-400 max-sm:flex-shrink-0">
        <Layers className="w-4 h-4" />
        <span className="text-xs font-medium whitespace-nowrap">
          {language === 'es' ? 'Remover Fondo' : 'Remove Background'}
        </span>
      </div>
      
      <div className="w-px h-5 bg-zinc-700 max-sm:flex-shrink-0" />
      
      {/* Selector de modo */}
      <div className="flex items-center gap-1 bg-zinc-700/30 rounded-lg p-0.5 max-sm:flex-shrink-0">
        <button
          data-testid="bg-mode-white"
          onClick={() => setMode('white')}
          className={`px-3 py-1.5 text-xs rounded-md transition-all font-medium whitespace-nowrap ${
            mode === 'white' 
              ? 'bg-white text-zinc-900 shadow-lg' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-600/50'
          }`}
          title={language === 'es' ? 'Fondo blanco puro' : 'Pure white background'}
        >
          {language === 'es' ? 'Fondo Blanco' : 'White BG'}
        </button>
        
        <button
          data-testid="bg-mode-transparent"
          onClick={() => setMode('transparent')}
          className={`px-3 py-1.5 text-xs rounded-md transition-all font-medium whitespace-nowrap ${
            mode === 'transparent' 
              ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-600/50'
          }`}
          title={language === 'es' ? 'Fondo transparente (PNG)' : 'Transparent background (PNG)'}
        >
          {language === 'es' ? 'Transparente' : 'Transparent'}
        </button>
        
        <button
          data-testid="bg-mode-replace"
          onClick={() => setMode('replace')}
          className={`px-3 py-1.5 text-xs rounded-md transition-all font-medium ${
            mode === 'replace' 
              ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-600/50'
          }`}
          title={language === 'es' ? 'Reemplazar fondo con IA' : 'Replace background with AI'}
        >
          {language === 'es' ? 'Reemplazar' : 'Replace'}
        </button>
      </div>
      
      {/* Input de prompt - Solo visible para replace */}
      {mode === 'replace' && (
        <>
          <div className="w-px h-5 bg-zinc-700 max-sm:flex-shrink-0" />
          <div className="flex items-center gap-2 max-sm:flex-shrink-0">
            <input
              type="text"
              value={backgroundPrompt}
              onChange={(e) => setBackgroundPrompt(e.target.value)}
              placeholder={language === 'es' ? 'Describe el nuevo fondo...' : 'Describe the new background...'}
              className="min-w-[160px] max-w-[300px] sm:min-w-[200px] px-3 py-1.5 bg-zinc-700/50 border border-zinc-600 rounded-lg text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
              data-testid="bg-replace-prompt"
            />
            <span className="text-[10px] text-amber-400 whitespace-nowrap">
              {language === 'es' ? '* Obligatorio' : '* Required'}
            </span>
          </div>
        </>
      )}
      
      <div className="w-px h-5 bg-zinc-700 max-sm:flex-shrink-0" />
      
      {/* Toggle controles avanzados */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        data-testid="bg-advanced-toggle"
        className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-all whitespace-nowrap max-sm:flex-shrink-0 ${
          showAdvanced 
            ? 'bg-zinc-600/50 text-violet-300' 
            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/30'
        }`}
        title={language === 'es' ? 'Controles avanzados de bordes' : 'Advanced edge controls'}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{language === 'es' ? 'Bordes' : 'Edges'}</span>
        <span className="sm:hidden text-[10px]">{language === 'es' ? 'Bordes' : 'Edges'}</span>
      </button>
      
      {/* Controles avanzados de bordes */}
      {showAdvanced && (
        <>
          <div className="w-px h-5 bg-zinc-700" />
          <div className="flex items-center gap-3">
            {/* Nitidez de borde */}
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] text-zinc-400 whitespace-nowrap">
                {language === 'es' ? 'Nitidez' : 'Sharpness'}
              </label>
              <input
                type="range"
                min="10"
                max="100"
                value={edgeSharpness}
                onChange={(e) => setEdgeSharpness(Number(e.target.value))}
                className="w-16 h-1 accent-violet-500"
                data-testid="bg-edge-sharpness"
              />
              <span className="text-[10px] text-zinc-500 w-6 text-right">{edgeSharpness}</span>
            </div>
            
            {/* Grosor outline (solo para white y replace) */}
            {mode !== 'transparent' && (
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] text-zinc-400 whitespace-nowrap">
                  {language === 'es' ? 'Contorno' : 'Outline'}
                </label>
                <input
                  type="range"
                  min="0"
                  max="3"
                  step="1"
                  value={outlineWidth}
                  onChange={(e) => setOutlineWidth(Number(e.target.value))}
                  className="w-12 h-1 accent-violet-500"
                  data-testid="bg-outline-width"
                />
                <span className="text-[10px] text-zinc-500 w-4 text-right">{outlineWidth}</span>
              </div>
            )}
          </div>
        </>
      )}
      
      <div className="w-px h-5 bg-zinc-700 max-sm:flex-shrink-0" />
      
      {/* Botón Aplicar */}
      <button
        onClick={handleApply}
        disabled={isLoading || (mode === 'replace' && !backgroundPrompt.trim())}
        data-testid="bg-apply-btn"
        className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg text-xs font-medium hover:from-violet-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg whitespace-nowrap max-sm:flex-shrink-0"
      >
        <Check className="w-4 h-4" />
        {language === 'es' ? 'Aplicar' : 'Apply'}
      </button>
    </div>
  );
};

export default BackgroundRemovalPanel;
